import React, {useEffect, useState} from "react";
import {Container, Row, Col, Image} from "react-bootstrap";
import {PortableText} from "@portabletext/react";
import {DONATION_URL} from "./constants";
import {getPageSections, urlFor} from "./lib/sanity";

const SITE_TITLE = "Association de Sauvegarde du Temple de Sommières (ASTS)";

const FALLBACK_IMAGES = {
  pourquoi: {
    src: "/images/temple2.jpg",
    alt: "Intérieur du temple de Sommières",
    rounded: true,
  },
  association: {
    src: "logo_officiel_ASTS.png",
    alt: "Logo de l'ASTS",
    rounded: false,
    logo: true,
  },
  genese: {
    src: "/images/ag.jpg",
    alt: "Assemblée générale de l'association",
    rounded: true,
  },
  histoire: {
    src: "/images/organ.jpg",
    alt: "Orgue du temple de Sommières",
    rounded: true,
  },
  cordeliers: {
    src: "/images/organ2.jpg",
    alt: "Détail de l'orgue du temple",
    rounded: true,
  },
};

function pickImage(sanityImage, fallbackKey) {
  const fallback = FALLBACK_IMAGES[fallbackKey];
  if (sanityImage && sanityImage.asset) {
    return {
      src: urlFor(sanityImage).width(900).auto("format").url(),
      alt: sanityImage.alt || fallback.alt,
      rounded: fallback.rounded,
      logo: fallback.logo,
    };
  }
  return fallback;
}

function SectionImage({image}) {
  return (
    <Image
      src={image.src}
      alt={image.alt}
      fluid
      rounded={image.rounded}
      className={`section-image${image.logo ? " logo-image" : ""}`}
    />
  );
}

function Home() {
  const [sections, setSections] = useState({});

  useEffect(() => {
    document.title = SITE_TITLE;
  }, []);

  useEffect(() => {
    let cancelled = false;
    getPageSections()
      .then((data) => {
        if (!cancelled) setSections(data || {});
      })
      .catch(() => {
        // En cas d'erreur réseau, on laisse sections vide → fallback titre/texte non-affiché.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const pourquoi = sections.pourquoi;
  const association = sections.association;
  const genese = sections.genese;
  const temple = sections.temple;

  const templeSousSections = (temple && temple.sousSections) || [];
  const historique = templeSousSections[0];
  const cordeliers = templeSousSections[1];

  return (
    <div className="home-page">
      <section className="hero">
        <div
          className="hero-image"
          style={{backgroundImage: "url(/images/temple.jpg)"}}
          role="img"
          aria-label="Façade du temple de Sommières"
        />
        <div className="hero-overlay">
          <Container>
            <h1 className="hero-title">Sauvegarde du Temple de Sommières</h1>
            <p className="hero-subtitle">
              Préserver un édifice du patrimoine cultuel et culturel de la
              ville.
            </p>
          </Container>
        </div>
      </section>

      <Container as="main" id="main-content" className="page-content">
        <section id="Etat" className="section">
          <Row className="g-4 align-items-center">
            <Col xs={12} md={6}>
              <h2 className="section-title">
                {pourquoi?.titre || "Pourquoi ce projet ?"}
              </h2>
              {pourquoi?.contenu && <PortableText value={pourquoi.contenu} />}
            </Col>
            <Col xs={12} md={6}>
              <SectionImage image={pickImage(pourquoi?.image, "pourquoi")} />
            </Col>
          </Row>
        </section>

        <section id="Lassociation" className="section">
          <Row className="g-4 align-items-center">
            <Col xs={12} md={5}>
              <SectionImage
                image={pickImage(association?.image, "association")}
              />
            </Col>
            <Col xs={12} md={7}>
              <h2 className="section-title">
                {association?.titre ||
                  "L'Association de Sauvegarde du Temple de Sommières"}
              </h2>
              {association?.contenu && (
                <PortableText value={association.contenu} />
              )}
            </Col>
          </Row>
        </section>

        <section id="Genese" className="section">
          <Row className="g-4 align-items-center">
            <Col xs={12} md={6} className="order-md-2">
              <SectionImage image={pickImage(genese?.image, "genese")} />
            </Col>
            <Col xs={12} md={6} className="order-md-1">
              <h2 className="section-title">
                {genese?.titre || "Genèse de l'association"}
              </h2>
              {genese?.contenu && <PortableText value={genese.contenu} />}
            </Col>
          </Row>
        </section>

        <section id="Histoire" className="section">
          <h2 className="section-title text-center">
            {temple?.titre || "Le Temple de Sommières"}
          </h2>

          <Row className="g-4 align-items-start mb-4">
            <Col xs={12} md={5}>
              <SectionImage image={pickImage(historique?.image, "histoire")} />
            </Col>
            <Col xs={12} md={7}>
              <h3 className="subsection-title">
                {historique?.titre || "Historique"}
              </h3>
              {historique?.contenu && (
                <PortableText value={historique.contenu} />
              )}
            </Col>
          </Row>

          <Row className="g-4 align-items-start">
            <Col xs={12} md={7}>
              <h3 className="subsection-title">
                {cordeliers?.titre || "Origine de l'Église des Cordeliers"}
              </h3>
              {cordeliers?.contenu && (
                <PortableText value={cordeliers.contenu} />
              )}
            </Col>
            <Col xs={12} md={5}>
              <SectionImage image={pickImage(cordeliers?.image, "cordeliers")} />
            </Col>
          </Row>
        </section>

        <section id="CommentSoutenir" className="section">
          <Row className="g-4 align-items-center">
            <Col xs={12} md={7}>
              <h2 className="section-title">Comment soutenir ce projet&nbsp;?</h2>
              <p>
                Le coût total des travaux est important et vous pouvez
                contribuer à cette cause en faisant un don. Chaque geste
                compte&nbsp;!
              </p>
              <p>
                Les dons sont à faire via la <strong>Fondation du
                Patrimoine</strong>. Ils ouvrent droit à une réduction d'impôt
                qui peut aller jusqu'à 75&nbsp;% du don.
              </p>
              <p>
                <a
                  href={DONATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Faire un don
                </a>
              </p>
              <p className="mt-3 mb-2">Ou en flashant ce QR code&nbsp;:</p>
              <Image
                src="QRCode_FDP.png"
                alt="QR Code Fondation du Patrimoine"
                className="qr-image"
              />
            </Col>
            <Col xs={12} md={5}>
              <Image
                src="logo_fondation_du_patrimoine_cmjn.png"
                alt="Logo Fondation du Patrimoine"
                fluid
                className="section-image logo-image"
              />
            </Col>
          </Row>
        </section>

        <section id="Contact" className="section">
          <h2 className="section-title">Contact</h2>
          <address className="contact-block">
            <strong>
              Association de Sauvegarde du Temple de Sommières
            </strong>
            <br />
            16&nbsp;route de Montpellier, 30250 Sommières
            <br />
            <a href="mailto:astsommieres@gmail.com">
              astsommieres@gmail.com
            </a>
            <br />
            M<sup>me</sup>&nbsp;Danièle Martinez (Présidente)
          </address>
        </section>
      </Container>
    </div>
  );
}

export default Home;
